import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableContainer,
  TablePagination,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Modal,
  Menu,
  MenuItem,
  Dialog, // <-- Added Dialog
  DialogTitle, // <-- Added DialogTitle
  DialogContent, // <-- Added DialogContent
  DialogActions, // <-- Added DialogActions
  Button,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"; // <-- Added Icon for success
import { styled } from "@mui/system";
import axios from "axios";
import { API_URL } from "../../Config/api";
import PackageEventDetails from "./PackageActionComponents/PackageEventDetails";
import PackageServicesDetails from "./PackageActionComponents/PackageServicesDetails";
import PackageReschedule from "./PackageActionComponents/PackageReschedule";
import PackageInvoice from "./PackageActionComponents/PackageInvoice";
import PackagePayment from "./PackageActionComponents/PackagePayment";
import PackageDocuments from "./PackageActionComponents/PackageDocuments";
import LongTermFormModal from "./PackageActionComponents/LongTermFormModal";
import ConsentFormModal from "./PackageActionComponents/ConsentFormModal";
import FeedbackModal from "./PackageActionComponents/FeedbackModal";
import PackageCancellation from "./PackageActionComponents/PackageCancellation";
import PackageJobClosure from "./PackageActionComponents/Packagejobclosure";
import { useNavigate } from "react-router-dom";

const OngoingServiceCard = styled(Card)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  marginTop: "10px",
  backgroundColor: "white",
  boxShadow: "4px 4px 10px 7px rgba(135, 135, 135, 0.05)",
  height: "55px",
  borderRadius: "10px",
  transition: "0.5s ease-in-out",
  "&:hover": {
    backgroundColor: "#F7F7F7",
    cursor: "pointer",
  },
});

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "70%",
  maxHeight: "90vh",
  bgcolor: "background.paper",
  borderRadius: "10px",
  boxShadow: 24,
  display: "flex",
  flexDirection: "column",
  overflow: "visible",
};

// --- PACKAGE SERVICES MODAL WRAPPER ---
const PackageServicesModalContent = ({
  eveId,
  onClose,
  accessToken,
  aggSpPtId,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    const fetchServicesDetails = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/packages/OnclickPackageName/?event_id=${eveId}`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          },
        );
        setData(response.data.data || []);
      } catch (error) {
        console.error("Error fetching package services:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    if (eveId) fetchServicesDetails();
  }, [eveId, accessToken]);

  return (
    <Box sx={modalStyle}>
      <Box
        sx={{
          background: "rgb(173, 219, 177)",
          color: "#fff",
          px: 3,
          py: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          zIndex: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.1rem" }}>
          Package Details
        </Typography>
        <IconButton onClick={onClose} sx={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{ p: 3, overflowY: "auto", scrollbarWidth: "thin", height: "100%" }}
      >
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            p={4}
            height="200px"
          >
            <CircularProgress />
          </Box>
        ) : (
          <PackageServicesDetails data={data} aggSpPtId={aggSpPtId} />
        )}
      </Box>
    </Box>
  );
};

// --- MAIN TABLE COMPONENT ---
const PackageTable = ({
  accessToken,
  formatDate,
  eventIDRequest,
  auth,
  filterType,
  searchValue,
  startDateTime,
}) => {
  const [packageData, setPackageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const navigate = useNavigate();
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [selectedEventEveId, setSelectedEventEveId] = useState(null);

  const [isPackageModalOpen, setIsPackageModalOpen] = useState(false);
  const [selectedPackageEveId, setSelectedPackageEveId] = useState(null);

  // ---> RESCHEDULE MODAL & MENU STATES <---
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [selectedRescheduleEveId, setSelectedRescheduleEveId] = useState(null);
  const [activeRowData, setActiveRowData] = useState(null);

  // invoice modal states
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [selectedInvoiceEveId, setSelectedInvoiceEveId] = useState(null);

  // payment modal states
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedPaymentEveId, setSelectedPaymentEveId] = useState(null);

  // cancellation modal states
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [selectedCancelEveId, setSelectedCancelEveId] = useState(null);
  const [selectedSessionCount, setSelectedSessionCount] = useState(0);
  // discharge summary modal states
  const [openDocument, setOpenDocument] = useState(false);
  const [documentType, setDocumentType] = useState("");

  // long term form modal states
  const [isLongTermOpen, setIsLongTermOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  // consent form modal states
  const [isConsentOpen, setIsConsentOpen] = useState(false);

  // add state near your other modal states
  const [isJobClosureOpen, setIsJobClosureOpen] = useState(false);


  // Material UI Menu States
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  // --- NEW: Payment Done Dialog State ---
  const [paymentDoneDialogOpen, setPaymentDoneDialogOpen] = useState(false);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCloseEventModal = () => {
    setIsEventModalOpen(false);
    setSelectedEventEveId(null);
  };

  const handleClosePackageModal = () => {
    setIsPackageModalOpen(false);
    setSelectedPackageEveId(null);
  };

  // ---> MENU HANDLERS <---
  const handleMenuClick = (event, row) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setActiveRowData(row);

    setSelectedSessionCount(row.session_count || 0);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRescheduleClick = () => {
    if (activeRowData) {
      setSelectedRescheduleEveId(activeRowData.eve_id);
      setIsRescheduleModalOpen(true);
      if (eventIDRequest) eventIDRequest(activeRowData.eve_id);
    }
    handleMenuClose();
  };

  const [eventIdProfessional, setEventIdProfessional] = useState(null);

  const handleProfessionalReschedule = () => {
    if (!activeRowData?.eve_id) return;

    const selectedEventId = activeRowData.eve_id;

    console.log("Professional reschedule package details:", activeRowData);

    setEventIdProfessional(selectedEventId);
    getRequestAllocation(selectedEventId);

    handleMenuClose();
  };

  const getRequestAllocation = async (eventId) => {
    if (!eventId) return;

    try {
      const res = await fetch(
        `${API_URL}/web/agg_hhc_srv_req_prof_allocate/${eventId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!res.ok) {
        throw new Error(`Request Allocation API failed: ${res.status}`);
      }

      const data = await res.json();

      console.log("Request Professional Allocation Response:", data);

      const eventValue = data?.Event_ID;
      const patientValue = data?.patient_details?.agg_sp_pt_id;
      const callerValue = data?.caller_details?.caller_id;
      const eventPlanValue = data?.POC?.[0]?.eve_poc_id;

      navigate("/viewservice", {
        state: {
          patientID: patientValue,
          callerID: callerValue,
          eventPlanID: eventPlanValue,
          eventID: eventValue,
          flag: 2,

          // Professional Reschedule Package
          isProfessionalReschedule: true,
        },
      });
    } catch (error) {
      console.error("Error fetching Request Allocation:", error);
    }
  };

  const handleCloseRescheduleModal = () => {
    setIsRescheduleModalOpen(false);
    setSelectedRescheduleEveId(null);
  };

  const handleInvoiceClick = () => {
    if (activeRowData) {
      setSelectedInvoiceEveId(activeRowData.eve_id);
      setIsInvoiceModalOpen(true);
      if (eventIDRequest) eventIDRequest(activeRowData.eve_id);
    }
    handleMenuClose();
  };

  const handleCancelClick = () => {
    if (activeRowData) {
      setSelectedCancelEveId(activeRowData.eve_id);
      setIsCancelModalOpen(true);
    }
    handleMenuClose();
  };

  const handleCloseCancelModal = () => {
    setIsCancelModalOpen(false);
    setSelectedCancelEveId(null);
  };




  const handleOpenJobClosure = () => {
    if (activeRowData) setIsJobClosureOpen(true);
    handleMenuClose();
  };
  // --- UPDATED: Payment Click Logic for Dialog ---
  const handlePaymentClick = () => {
    if (activeRowData) {
      // Check if remaining amount is zero or less
      const remaining = Number(activeRowData.remaining_amount) || 0;

      if (remaining <= 0) {
        // Show Dialog instead of Snackbar
        setPaymentDoneDialogOpen(true);
      } else {
        // Open modal for partial/remaining payment
        setSelectedPaymentEveId(activeRowData.eve_id);
        setIsPaymentModalOpen(true);
        if (eventIDRequest) eventIDRequest(activeRowData.eve_id);
      }
    }
    handleMenuClose();
  };

  const handleClosePaymentModal = () => {
    setIsPaymentModalOpen(false);
    setSelectedPaymentEveId(null);
    fetchPackages(); // Optional: Refresh data when modal closes to update remaining balance
  };

  const handleOpenDocument = () => {
    if (activeRowData) {
      setDocumentType("Discharge Summary");
      setOpenDocument(true);
    }
    handleMenuClose();
  };

  const handleCloseDocument = () => {
    setOpenDocument(false);
    setDocumentType("");
  };

  const handleOpenLongTermForm = () => {
    if (activeRowData) {
      setIsLongTermOpen(true);
    }
    handleMenuClose();
  };

  const handleOpenConsentForm = () => {
    if (activeRowData) {
      setIsConsentOpen(true);
    }
    handleMenuClose();
  };

  const handleOpenFeedback = () => {
    if (activeRowData) {
      setIsFeedbackOpen(true);
    }
    handleMenuClose();
  };

  const fetchPackages = useCallback(async () => {
    setLoading(true);
    try {
      const apiUrl = `${API_URL}/packages/Ongoing_Event/`;
      let queryParams = {};

      if (filterType === "eventCode" && searchValue)
        queryParams.eve_code = searchValue;
      else if (filterType === "ptnPhn" && searchValue)
        queryParams.patient_no = searchValue;
      else if (filterType === "ptnName" && searchValue)
        queryParams.patient_name = searchValue;
      else if (filterType === "clrPhn" && searchValue)
        queryParams.caller_no = searchValue;
      else if (filterType === "profName" && searchValue)
        queryParams.prof_name = searchValue;

      if (startDateTime) queryParams.date = startDateTime;

      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        params: queryParams,
      });

      if (
        response.data?.msg === "data not foundf" ||
        response.data?.msg === "data not found"
      ) {
        setPackageData([]);
      } else {
        setPackageData(response.data || []);
      }
      setPage(0);
    } catch (error) {
      console.error("Error fetching packages:", error);
      setPackageData([]);
    } finally {
      setLoading(false);
    }
  }, [accessToken, filterType, searchValue, startDateTime]);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  return (
    <>
      <TableContainer
        sx={{
          height:
            packageData.length === 0 || packageData.length < 5
              ? "60vh"
              : "auto",
          overflowY: "hidden !important",
          mt: 1,
        }}
      >
        <Table sx={{ minWidth: 1100, overflowY: "hidden" }}>
          <TableHead>
            <TableRow>
              <OngoingServiceCard
                style={{
                  background: "rgb(173, 219, 177)",
                  color: "#423737",
                  borderRadius: "8px 10px 0 0",
                  height: "50px",
                }}
              >
                <CardContent
                  style={{ flex: 1.5, borderRight: "1px solid #ffffff" }}
                >
                  <Typography style={{ fontSize: "13px", fontWeight: 600 }}>
                    Event Code
                  </Typography>
                </CardContent>
                <CardContent
                  style={{ flex: 1.5, borderRight: "1px solid #ffffff" }}
                >
                  <Typography style={{ fontSize: "13px", fontWeight: 600 }}>
                    Patient Name
                  </Typography>
                </CardContent>
                <CardContent
                  style={{ flex: 1.3, borderRight: "1px solid #ffffff" }}
                >
                  <Typography style={{ fontSize: "13px", fontWeight: 600 }}>
                    Caller No
                  </Typography>
                </CardContent>
                <CardContent
                  style={{ flex: 1.5, borderRight: "1px solid #FFFFFF" }}
                >
                  <Typography style={{ fontSize: "13px", fontWeight: 600 }}>
                    Package Name
                  </Typography>
                </CardContent>
                <CardContent
                  style={{ flex: 1.2, borderRight: "1px solid #FFFFFF" }}
                >
                  <Typography style={{ fontSize: "13px", fontWeight: 600 }}>
                    Start Date
                  </Typography>
                </CardContent>
                <CardContent
                  style={{ flex: 1.2, borderRight: "1px solid #FFFFFF" }}
                >
                  <Typography style={{ fontSize: "13px", fontWeight: 600 }}>
                    End Date
                  </Typography>
                </CardContent>

                {/* --- MERGED HEADER: PAYMENT STATUS --- */}
                <CardContent
                  style={{ flex: 1.5, borderRight: "1px solid #FFFFFF" }}
                >
                  <Typography style={{ fontSize: "13px", fontWeight: 600 }}>
                    Pay Status
                  </Typography>
                </CardContent>
                <CardContent
                  style={{ flex: 0.5, borderRight: "1px solid #FFFFFF" }}
                >
                  <Typography style={{ fontSize: "13px", fontWeight: 600 }}>
                    Job Closure
                  </Typography>
                </CardContent>

                <CardContent
                  style={{ flex: 1, borderRight: "1px solid #FFFFFF" }}
                >
                  <Typography style={{ fontSize: "13px", fontWeight: 600 }}>
                    Added By
                  </Typography>
                </CardContent>
                <CardContent style={{ flex: 0.5 }}>
                  <Typography style={{ fontSize: "13px", fontWeight: 600 }}>
                    Action
                  </Typography>
                </CardContent>
              </OngoingServiceCard>
            </TableRow>
          </TableHead>

          {loading ? (
            <Box
              sx={{
                display: "flex",
                mt: 10,
                justifyContent: "center",
                height: "130px",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <TableBody>
              {packageData.length === 0 ? (
                <TableRow>
                  <CardContent>
                    <Typography variant="body2" sx={{ p: 2 }}>
                      {searchValue
                        ? `No packages found for ${searchValue}`
                        : "No Package Data Available"}
                    </Typography>
                  </CardContent>
                </TableRow>
              ) : (
                packageData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        height: "auto",
                      }}
                    >
                      <OngoingServiceCard
                        style={{ height: "3.5rem" }}
                        sx={{
                          background: "#f1f1f1",
                          borderLeft: "4px solid transparent",
                          transition: "all 0.3s ease-in-out",
                          "&:hover": {
                            borderLeft: "4px solid rgb(19 166 31)",
                            borderRight: "4px solid rgb(19 166 31)",
                            transform: "scale(1)",
                          },
                        }}
                      >
                        {/* EVENT CODE */}
                        <CardContent style={{ flex: 1.5 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              textDecoration: "underline",
                              fontWeight: 500,
                              color: "#1976d2",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              setActiveRowData(row);
                              if (eventIDRequest) eventIDRequest(row.eve_id);
                              setSelectedEventEveId(row.eve_id);
                              setIsEventModalOpen(true);
                            }}
                          >
                            {row.event_code}
                          </Typography>
                        </CardContent>

                        {/* PATIENT NAME */}
                        <CardContent
                          style={{
                            flex: 1.5,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <Tooltip title={row.patient_name || ""}>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "13px" }}
                            >
                              {row.patient_name || "-"}
                            </Typography>
                          </Tooltip>
                        </CardContent>

                        {/* CALLER NO */}
                        <CardContent style={{ flex: 1.3 }}>
                          <Typography variant="body2" sx={{ fontSize: "13px" }}>
                            {row.caller_no || "-"}
                          </Typography>
                        </CardContent>

                        {/* PACKAGE NAME */}
                        <CardContent
                          style={{
                            flex: 1.5,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <Tooltip title={row.package_name || "N/A"}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "13px",
                                textDecoration: "underline",
                                color: "#1976d2",
                                cursor: "pointer",
                                fontWeight: 500,
                              }}
                              onClick={() => {
                                setActiveRowData(row);
                                setSelectedPackageEveId(row.eve_id);
                                setIsPackageModalOpen(true);
                              }}
                            >
                              {row.package_name || "-"}
                            </Typography>
                          </Tooltip>
                        </CardContent>

                        {/* START DATE */}
                        <CardContent style={{ flex: 1.2 }}>
                          <Typography variant="body2" sx={{ fontSize: "13px" }}>
                            {row.startDate
                              ? formatDate
                                ? formatDate(row.startDate)
                                : row.startDate
                              : "-"}
                          </Typography>
                        </CardContent>

                        {/* END DATE */}
                        <CardContent style={{ flex: 1.2 }}>
                          <Typography variant="body2" sx={{ fontSize: "13px" }}>
                            {row.endDate
                              ? formatDate
                                ? formatDate(row.endDate)
                                : row.endDate
                              : "-"}
                          </Typography>
                        </CardContent>

                        {/* <CardContent style={{ flex: 1.5 }}>
                          <Tooltip
                            title={`Total: ₹${row.total_amount || 0} | Pending: ₹${row.remaining_amount || 0}`}
                            arrow
                            placement="top"
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "13px",
                                fontWeight: 600,
                                cursor: "help",
                                color:
                                  row.remaining_amount > 0
                                    ? "error.main"
                                    : "success.main",
                              }}
                            >
                              {row.remaining_amount || 0} /{" "}
                              {row.total_amount || 0}
                            </Typography>
                          </Tooltip>
                        </CardContent> */}

                        <CardContent style={{ flex: 1.5 }}>
                          {row.discount_type_name?.toLowerCase() === "complimentary" ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                color: "success.main",
                                fontSize: "13px",
                                fontWeight: 600,
                              }}
                            >
                              <CheckCircleIcon sx={{ fontSize: 18 }} />
                              Complimentary
                            </Box>
                          ) : (
                            <Tooltip
                              title={`Total: ₹${row.total_amount || 0} | Pending: ₹${row.remaining_amount || 0
                                }`}
                              arrow
                              placement="top"
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "13px",
                                  fontWeight: 600,
                                  cursor: "help",
                                  color:
                                    row.remaining_amount > 0
                                      ? "error.main"
                                      : "success.main",
                                }}
                              >
                                {
                                  row.received_by === null ? (
                                    <>
                                      {row.remaining_amount || 0} / {row.total_amount || 0}
                                    </>
                                  ) : (
                                    <>
                                      {
                                        row.received_by === 1 ? (
                                          <>
                                            Received By Professional
                                          </>
                                        ) : row.received_by === 2 ? (
                                          <>
                                            Received To Spero
                                          </>
                                        ) : row.received_by === 3 ? (
                                          <>
                                            Received By Desk
                                          </>
                                        ) : (
                                          <>
                                            0
                                          </>
                                        )
                                      }
                                    </>
                                  )
                                }
                              </Typography>
                            </Tooltip>
                          )}
                        </CardContent>

                        {/* JOB CLOSURE */}
                        <CardContent
                          style={{
                            flex: 0.5,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          {row.closure_status ? (
                            <Tooltip
                              title="Job Closure (Done)"
                              arrow
                              placement="top"
                            >
                              <CheckCircleIcon
                                sx={{
                                  fontSize: "22px",
                                  color: "success.main",
                                  cursor: "pointer",
                                }}
                              />
                            </Tooltip>
                          ) : (
                            <Tooltip
                              title="Job Closure (Pending)"
                              arrow
                              placement="top"
                            >
                              <CancelIcon
                                sx={{
                                  fontSize: "22px",
                                  color: "error.main",
                                  cursor: "pointer",
                                }}
                              />
                            </Tooltip>
                          )}
                        </CardContent>

                        {/* ADDED BY */}
                        <CardContent style={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontSize: "13px" }}>
                            {row.added_by || "-"}
                          </Typography>
                        </CardContent>

                        {/* ACTIONS MENU */}
                        <CardContent style={{ flex: 0.5 }}>
                          {auth && (
                            <IconButton
                              size="small"
                              onClick={(event) => {
                                handleMenuClick(event, row);
                              }}
                            >
                              <MoreHorizIcon
                                style={{ fontSize: "18px", cursor: "pointer" }}
                              />
                            </IconButton>
                          )}
                        </CardContent>
                      </OngoingServiceCard>
                    </TableRow>
                  ))
              )}
            </TableBody>
          )}
        </Table>
      </TableContainer>

      <TablePagination
        sx={{ overflowY: "hidden" }}
        rowsPerPageOptions={[5, 10, 25, 100]}
        component="div"
        count={packageData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* --- MENU DROPDOWN --- */}
      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 2,
          sx: { mt: 0.5, borderRadius: "8px" },
        }}
      >
        <MenuItem onClick={handleRescheduleClick} sx={{ fontSize: "14px" }}>
          Package Reschedule
        </MenuItem>
        <MenuItem onClick={handleProfessionalReschedule} sx={{ fontSize: "14px" }}>
          Professional Reschedule
        </MenuItem>
        <MenuItem onClick={handleInvoiceClick} sx={{ fontSize: "14px" }}>
          Package Invoice
        </MenuItem>

        <MenuItem onClick={handlePaymentClick} sx={{ fontSize: "14px" }}>
          Make Payment
        </MenuItem>

        <MenuItem onClick={handleOpenJobClosure} sx={{ fontSize: "14px" }}>
          Job Closure
        </MenuItem>
        <MenuItem onClick={handleCancelClick} sx={{ fontSize: "14px" }}>
          Package Cancellation
        </MenuItem>
        <MenuItem onClick={handleOpenDocument} sx={{ fontSize: "14px" }}>
          Discharge Summary
        </MenuItem>
        <MenuItem onClick={handleOpenFeedback} sx={{ fontSize: "14px" }}>
          Feedback
        </MenuItem>
        <MenuItem onClick={handleOpenLongTermForm} sx={{ fontSize: "14px" }}>
          Long Term Form
        </MenuItem>
        <MenuItem onClick={handleOpenConsentForm} sx={{ fontSize: "14px" }}>
          Consent Form
        </MenuItem>
      </Menu>

      {/* --- EVENT DETAILS MODAL --- */}
      <Modal open={isEventModalOpen} onClose={handleCloseEventModal}>
        <div>
          {selectedEventEveId && (
            <PackageEventDetails
              eveID={selectedEventEveId}
              aggSpPtId={activeRowData?.agg_sp_pt_id}
              onClose={handleCloseEventModal}
              discountTypeName={activeRowData?.discount_type_name}
            />
          )}
        </div>
      </Modal>

      {/* --- PACKAGE MODAL --- */}
      <Modal
        open={isPackageModalOpen}
        onClose={handleClosePackageModal}
        sx={{ overflowX: "hidden" }}
      >
        <Box sx={{ outline: "none", overflowX: "hidden" }}>
          {selectedPackageEveId && (
            <PackageServicesModalContent
              eveId={selectedPackageEveId}
              aggSpPtId={activeRowData?.agg_sp_pt_id}
              onClose={handleClosePackageModal}
              accessToken={accessToken}
            />
          )}
        </Box>
      </Modal>

      {/* ---> RESCHEDULE MODAL <--- */}
      <Modal open={isRescheduleModalOpen} onClose={handleCloseRescheduleModal}>
        <Box sx={{ ...modalStyle, width: "auto", p: 0 }}>
          <Box
            sx={{
              background: "rgb(173, 219, 177)",
              color: "#fff",
              px: 3,
              py: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, fontSize: "1.1rem" }}
            >
              Reschedule Package
            </Typography>
            <IconButton
              onClick={handleCloseRescheduleModal}
              sx={{ color: "#fff" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ p: 3, backgroundColor: "white" }}>
            {selectedRescheduleEveId && (
              <PackageReschedule
                eventID={selectedRescheduleEveId}
                aggSpPtId={activeRowData?.agg_sp_pt_id}
                accessToken={accessToken}
                onClose={handleCloseRescheduleModal}
                fetchPackages={fetchPackages}
                rowData={activeRowData}
              />
            )}
          </Box>
        </Box>
      </Modal>

      {/* ---> INVOICE MODAL <--- */}
      <Modal
        open={isInvoiceModalOpen}
        onClose={() => setIsInvoiceModalOpen(false)}
      >
        <Box sx={{ outline: "none" }}>
          {selectedInvoiceEveId && (
            <PackageInvoice
              eveID={selectedInvoiceEveId}
              aggSpPtId={activeRowData?.agg_sp_pt_id}
              onClose={() => setIsInvoiceModalOpen(false)}
            />
          )}
        </Box>
      </Modal>

      {/* ---> PAYMENT MODAL <--- */}
      <Modal open={isPaymentModalOpen} onClose={handleClosePaymentModal}>
        <Box sx={{ ...modalStyle, width: "auto", minWidth: "500px", p: 0 }}>
          <Box
            sx={{
              background: "rgb(173, 219, 177)",
              color: "#fff",
              px: 3,
              py: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, fontSize: "1.1rem" }}
            >
              Make Payment
            </Typography>
            <IconButton
              onClick={handleClosePaymentModal}
              sx={{ color: "#fff" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box
            sx={{
              p: 1,
              backgroundColor: "white",
              maxHeight: "80vh",
              overflowY: "auto",
            }}
          >
            {selectedPaymentEveId && activeRowData && (
              <PackagePayment
                eveId={selectedPaymentEveId}
                aggSpPtId={activeRowData?.agg_sp_pt_id}
                callerId={activeRowData.caller_id || activeRowData.caller_no}
                summaryData={{
                  patientName: activeRowData.patient_name,
                  packageName: activeRowData.package_name,
                  totalAmount: activeRowData.total_amount,
                  remainingAmount: activeRowData.remaining_amount,
                }}
                onClose={handleClosePaymentModal}
              />
            )}
          </Box>
        </Box>
      </Modal>

      {/* ---> DISCHARGE SUMMARY MODAL <--- */}
      <Modal open={openDocument} onClose={handleCloseDocument}>
        <Box
          sx={{
            ...modalStyle,
            width: 550,
            p: 0,
            borderRadius: "10px",
            overflow: "auto",
          }}
        >
          <Box
            sx={{
              background: "rgb(173, 219, 177)",
              color: "#FFFFFF",
              px: 3,
              py: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>
              {documentType}
            </Typography>
            <IconButton onClick={handleCloseDocument} sx={{ color: "#FFFFFF" }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ p: 2, maxHeight: "70vh", overflowY: "auto" }}>
            {activeRowData && (
              <PackageDocuments
                eventID={activeRowData.eve_id}
                callerId={activeRowData.caller_id || activeRowData.caller_no}
                patientID={activeRowData?.agg_sp_pt_id}
                docType={documentType}
              />
            )}
          </Box>
        </Box>
      </Modal>

      {/* ---> PACKAGE CANCEL MODAL <--- */}
      <Modal open={isCancelModalOpen} onClose={handleCloseCancelModal}>
        <Box sx={{ ...modalStyle, width: "400px", p: 0 }}>
          <Box
            sx={{
              background: "rgb(173, 219, 177)",
              color: "#fff",
              px: 3,
              py: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, fontSize: "1.1rem" }}
            >
              Cancel Package
            </Typography>
            <IconButton onClick={handleCloseCancelModal} sx={{ color: "#fff" }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ p: 3, backgroundColor: "white" }}>
            {selectedCancelEveId && (
              <PackageCancellation
                eventID={selectedCancelEveId}
                aggSpPtId={activeRowData?.agg_sp_pt_id}
                subSrvID={activeRowData?.sub_srv_id || ""}
                onClose={handleCloseCancelModal}
                fetchPackages={fetchPackages}
                sessionCount={selectedSessionCount}
              />
            )}
          </Box>
        </Box>
      </Modal>

      {/* --- LONG TERM FORM MODAL --- */}
      {isLongTermOpen && (
        <LongTermFormModal
          open={isLongTermOpen}
          onClose={() => setIsLongTermOpen(false)}
          eventID={activeRowData?.eve_id}
          aggSpPtId={activeRowData?.agg_sp_pt_id}
          accessToken={accessToken}
        />
      )}

      {/* --- CONSENT FORM MODAL --- */}
      {isConsentOpen && (
        <ConsentFormModal
          open={isConsentOpen}
          onClose={() => setIsConsentOpen(false)}
          eventID={activeRowData?.eve_id}
          aggSpPtId={activeRowData?.agg_sp_pt_id}
          isConsentFilled={activeRowData?.is_concent_form_filled}
        />
      )}

      {/* --- FEEDBACK MODAL --- */}
      {isFeedbackOpen && (
        <FeedbackModal
          open={isFeedbackOpen}
          onClose={() => setIsFeedbackOpen(false)}
          eventID={activeRowData?.eve_id}
          aggSpPtId={activeRowData?.agg_sp_pt_id}
          patientID={
            activeRowData?.patient_id || activeRowData?.agg_sp_pt_id || ""
          }
          jobClosureCount={activeRowData?.job_closure?.job_closure_count}
          totalSessionCount={activeRowData?.job_closure?.total_session}
        />
      )}

      <Modal open={isJobClosureOpen} onClose={() => setIsJobClosureOpen(false)}>
        <Box
          sx={{
            ...modalStyle,
            width: "95vw",
            height: "95vh",
            maxWidth: "1600px",
            p: 0,
          }}
        >
          {activeRowData && (
            <PackageJobClosure
              eventID={activeRowData.eve_id}
              dtlsId={activeRowData.dtls_id}
              shift={activeRowData.shift}
              accessToken={accessToken}
              defaultStartDate={activeRowData.startDate}
              defaultEndDate={activeRowData.endDate}
              onClose={() => setIsJobClosureOpen(false)}
              onSubmitted={fetchPackages}
            />
          )}
        </Box>
      </Modal>

      {/* --- PAYMENT ALREADY DONE DIALOG --- */}
      <Dialog
        open={paymentDoneDialogOpen}
        onClose={() => setPaymentDoneDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            minWidth: "280px",
            maxWidth: "320px",
            textAlign: "center",
            p: 0.5,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <CheckCircleOutlineIcon
            sx={{ fontSize: 46, color: "#10b981", mb: 0.5 }} // Icon size 60 se 46 kiya
          />
          <Typography
            variant="h6" // h5 se h6 kiya taaki heading thodi choti dikhe
            sx={{ fontWeight: "bold", color: "#1e293b" }}
          >
            Payment Complete!
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pb: 1.5 }}>
          <Typography variant="body2" sx={{ color: "#475569" }}>
            {" "}
            {/* body1 se body2 kiya */}
            The total amount for this package has already been paid. There is no
            pending balance.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 2.5 }}>
          <Button
            variant="contained"
            size="small" // Button size thoda chota kiya
            onClick={() => setPaymentDoneDialogOpen(false)}
            sx={{
              background: "linear-gradient(90deg, #10b981 0%, #059669 100%)",
              borderRadius: "20px",
              px: 3.5, // Button ki width thodi kam ki
              textTransform: "none",
              fontWeight: 600,
              "&:hover": {
                background: "linear-gradient(90deg, #059669 0%, #047857 100%)",
              },
            }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PackageTable;
